import api from './api.service';
import type { ApiResponse } from '@/types';
import {
  normalizeEodEligibleStage,
  type EodCloseStageInput,
  type EodCloseStageResult,
  type EodEligibleStage,
} from '@/utils/eodClose';
import { workOrderStageService } from './qc-workflow.service';

export const EOD_CLOSE_QUERY_KEY = 'mfg-eod-close' as const;

export type EodEligibleFilters = {
  search?: string;
  work_order_id?: string;
  workstation_id?: string;
  operator_user_id?: string;
  status?: string;
  /** Also list Completed stages (any work order, closed ones too) for log-only back entries. */
  include_completed?: boolean;
  limit?: number;
  offset?: number;
};

function unwrapList(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') {
    const d = payload as Record<string, unknown>;
    if (Array.isArray(d.data)) return d.data;
    if (Array.isArray(d.items)) return d.items;
  }
  return [];
}

export async function listEodEligibleStages(
  filters: EodEligibleFilters = {}
): Promise<EodEligibleStage[]> {
  const params = new URLSearchParams();
  if (filters.search?.trim()) params.set('search', filters.search.trim());
  if (filters.work_order_id) params.set('work_order_id', filters.work_order_id);
  if (filters.workstation_id) params.set('workstation_id', filters.workstation_id);
  if (filters.operator_user_id) params.set('operator_user_id', filters.operator_user_id);
  if (filters.status) params.set('status', filters.status);
  if (filters.include_completed) params.set('include_completed', '1');
  if (filters.limit != null) params.set('limit', String(filters.limit));
  if (filters.offset != null) params.set('offset', String(filters.offset));
  const qs = params.toString();
  const res: ApiResponse<unknown> = await api.get(
    `/manufacturing/stages/eod-close/eligible${qs ? `?${qs}` : ''}`
  );
  const raw = unwrapList(res.data ?? res);
  return raw
    .filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
    .map((row) => normalizeEodEligibleStage(row))
    .filter((s) => !!s.id);
}

export async function submitEodCloseBatch(
  items: EodCloseStageInput[]
): Promise<{ results: EodCloseStageResult[] }> {
  const res: ApiResponse<{ results?: EodCloseStageResult[] }> = await api.post(
    '/manufacturing/stages/eod-close',
    { items }
  );
  const data = (res.data ?? res) as { results?: EodCloseStageResult[] };
  return { results: Array.isArray(data.results) ? data.results : [] };
}

export async function getStageForEodLog(
  stageId: string
): Promise<{ notes: string | null; actual_hours: number | null }> {
  const response = await workOrderStageService.getById(stageId);
  const raw = (response.data?.data ?? response.data ?? {}) as Record<string, unknown>;
  const notes = raw.notes != null ? String(raw.notes) : null;
  const h = raw.actual_hours ?? raw.actualHours;
  const actual_hours =
    h == null || h === '' ? null : Number.isFinite(Number(h)) ? Number(h) : null;
  return { notes, actual_hours };
}

export const eodCloseService = {
  listEligible: listEodEligibleStages,
  submitBatch: submitEodCloseBatch,
  getStage: getStageForEodLog,
};
